import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useCurrentUser } from "../auth";
import { Button, Alert, Loading } from "./ui";
import {
  BILLING_MANAGE_PERMISSION,
  BILLING_PURCHASE_SUBSCRIPTION_PERMISSION,
  ORGANIZATION_EDIT_PERMISSION,
} from "../constants/permissions";
export default function AccessNotice({
  kind,
}: {
  kind: "setup" | "subscription";
}) {
  const { t } = useTranslation("ui");
  const { logout } = useAuth();
  const { currentUser, isCurrentUserLoading, reloadCurrentUser, permissions } =
    useCurrentUser();
  const canManage =
    kind === "setup"
      ? permissions.includes(ORGANIZATION_EDIT_PERMISSION)
      : permissions.some((permission) =>
          [
            BILLING_MANAGE_PERMISSION,
            BILLING_PURCHASE_SUBSCRIPTION_PERMISSION,
          ].includes(permission),
        );
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const check = async () => {
    setPending(true);
    setFailed(false);
    const user = await reloadCurrentUser();
    setPending(false);
    if (!user) setFailed(true);
    else if (
      !(kind === "setup"
        ? user.organization_setup_required
        : user.organization_subscription_required)
    )
      navigate("/products", { replace: true });
  };
  const adminEmail = import.meta.env.VITE_APP_ADMIN_EMAIL as string | undefined;
  if (isCurrentUserLoading) return <Loading label={t("profileLoading")} />;
  if (!currentUser)
    return (
      <Alert variant="error" title={t("profileError")}>
        <p>{t("profileErrorText")}</p>
        <Button variant="outline" onClick={() => void check()}>
          {t("retry")}
        </Button>
      </Alert>
    );
  const required =
    kind === "setup"
      ? currentUser.organization_setup_required
      : currentUser.organization_subscription_required;
  // A bookmarked notice cannot override the current server-derived access state.
  // Setup takes priority when both requirements are present.
  if (
    !required ||
    (kind === "subscription" && currentUser.organization_setup_required)
  )
    return <Navigate to="/products" replace />;
  return (
    <section className="vl-public-content">
      <span className="vl-eyebrow">Valid Labels</span>
      <h1>{t(kind === "setup" ? "setupTitle" : "subscriptionTitle")}</h1>
      <p>
        {t(
          kind === "setup"
            ? canManage
              ? "setupAdminText"
              : "setupText"
            : canManage
              ? "subscriptionAdminText"
              : "subscriptionText",
        )}
      </p>
      {failed && <Alert variant="error">{t("profileErrorText")}</Alert>}
      {canManage ? (
        <Button asChild>
          <Link
            to={
              kind === "setup"
                ? "/settings?section=organization"
                : "/settings?section=billing"
            }
          >
            {t(kind === "setup" ? "openSetup" : "openBilling")}
          </Link>
        </Button>
      ) : (
        <Button onClick={() => void check()} disabled={pending}>
          {t(pending ? "checking" : "checkAgain")}
        </Button>
      )}
      <div className="vl-row">
        {canManage && (
          <Button
            variant="ghost"
            onClick={() => void check()}
            disabled={pending}
          >
            {t(pending ? "checking" : "checkAgain")}
          </Button>
        )}
        <Button variant="ghost" onClick={logout}>
          {t("signOut")}
        </Button>
      </div>
      {canManage && adminEmail && (
        <a href={`mailto:${adminEmail}`}>
          {t("contactEmail", { email: adminEmail })}
        </a>
      )}
    </section>
  );
}
